const API_URL =
  process.env.REACT_APP_API_URL ||
  (window.location.protocol === "https:"
    ? "/"
    : "http://192.168.128.151:8000/");

function getAccessToken() {
  return localStorage.getItem("access");
}

function getRefreshToken() {
  return localStorage.getItem("refresh");
}

function setAccessToken(token) {
  localStorage.setItem("access", token);
}

function clearTokens() {
  localStorage.removeItem("access");
  localStorage.removeItem("refresh");
}

function isPublicEndpoint(endpoint) {
  return (
    endpoint.startsWith("accounts/login/") || 
    endpoint.startsWith("token/") ||
    endpoint.startsWith("token/refresh/") ||
    // 2FA用
    endpoint.startsWith("accounts/2fa/")
  );
}

function ensureAuthenticatedRequest(endpoint) {
  const access = getAccessToken();
  const refresh = getRefreshToken();

  if (isPublicEndpoint(endpoint)) return;

  if (!access && !refresh) {
    const error = new Error("未ログインのためリクエスト中止");
    error.status = 401;
    error.skipRequest = true;
    throw error;
  }
}

let navigateFunction = null;

export const setNavigateFunction = (navigate) => {
  navigateFunction = navigate;
};

function nowMs() {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

function safePreview(data) {
  try {
    if (data == null) return data;
    if (typeof data === "string") {
      return data.length > 300 ? `${data.slice(0, 300)}...` : data;
    }
    return JSON.parse(JSON.stringify(data));
  } catch {
    return "[unserializable]";
  }
}

/**
 * refresh 多重実行防止用
 * - 進行中の refresh があればそれを待つ
 * - なければ新規に refresh を開始
 */
let refreshPromise = null;

async function refreshToken(signal) {
  const startedAt = nowMs();
  const refresh = getRefreshToken();

  console.log("[api.refreshToken] START", {
    hasRefresh: !!refresh,
  });

  if (!refresh) {
    clearTokens();
    if (navigateFunction) navigateFunction("/login");

    const error = new Error("No refresh token");
    error.status = 401;

    console.error("[api.refreshToken] NO_REFRESH_TOKEN", {
      ms: Math.round(nowMs() - startedAt),
    });

    throw error;
  }

  const response = await fetch(`${API_URL}token/refresh/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh }),
    signal,
  });

  console.log("[api.refreshToken] RESPONSE", {
    status: response.status,
    ok: response.ok,
    ms: Math.round(nowMs() - startedAt),
  });

  if (!response.ok) {
    clearTokens();
    if (navigateFunction) navigateFunction("/login");

    const error = new Error("Failed to refresh token");
    error.status = 401;

    console.error("[api.refreshToken] FAILED", {
      status: response.status,
      ms: Math.round(nowMs() - startedAt),
    });

    throw error;
  }

  const data = await response.json();
  setAccessToken(data.access);

  console.log("[api.refreshToken] SUCCESS", {
    ms: Math.round(nowMs() - startedAt),
  });

  return data.access;
}

async function getRefreshedToken(signal) {
  if (!refreshPromise) {
    console.log("[api.refreshToken] CREATE_SHARED_PROMISE");

    refreshPromise = refreshToken(signal)
      .catch((error) => {
        throw error;
      })
      .finally(() => {
        console.log("[api.refreshToken] CLEAR_SHARED_PROMISE");
        refreshPromise = null;
      });
  } else {
    console.log("[api.refreshToken] JOIN_EXISTING_REFRESH");
  }

  return refreshPromise;
}

export async function apiRequest(
  endpoint,
  method = "GET",
  data = null,
  extraHeaders = {},
  retry = true,
  signal = null
) {
  const startedAt = nowMs();

  try {
    ensureAuthenticatedRequest(endpoint);
  } catch (error) {
    console.error("[apiRequest] AUTH_BLOCKED", {
      endpoint,
      method,
      message: error.message,
      status: error.status,
      skipRequest: error.skipRequest,
      ms: Math.round(nowMs() - startedAt),
    });
    throw error;
  }

  let accessToken = getAccessToken();

  const headers = {
    "Content-Type": "application/json",
    ...extraHeaders,
  };

  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  const options = {
    method,
    headers,
    credentials: "include",
    signal,
  };

  if (data) {
    options.body = JSON.stringify(data);
  }

  console.log("[apiRequest] START", {
    endpoint,
    method,
    retry,
    hasAccessToken: !!accessToken,
    hasRefreshToken: !!getRefreshToken(),
    data: safePreview(data),
  });

  let response;

  try {
    response = await fetch(`${API_URL}${endpoint}`, options);
  } catch (error) {
    console.error("[apiRequest] FETCH_ERROR", {
      endpoint,
      method,
      message: error?.message,
      name: error?.name,
      ms: Math.round(nowMs() - startedAt),
    });
    throw error;
  }

  console.log("[apiRequest] FIRST_RESPONSE", {
    endpoint,
    method,
    status: response.status,
    ok: response.ok,
    ms: Math.round(nowMs() - startedAt),
  });

  if (response.status === 401 && retry && !isPublicEndpoint(endpoint)) {
    console.warn("[apiRequest] 401_DETECTED -> TRY_REFRESH", {
      endpoint,
      method,
      ms: Math.round(nowMs() - startedAt),
    });

    try {
      accessToken = await getRefreshedToken(signal);
      headers["Authorization"] = `Bearer ${accessToken}`;
      options.headers = headers;

      const retryStartedAt = nowMs();
      response = await fetch(`${API_URL}${endpoint}`, options);

      console.log("[apiRequest] RETRY_RESPONSE", {
        endpoint,
        method,
        status: response.status,
        ok: response.ok,
        retryMs: Math.round(nowMs() - retryStartedAt),
        totalMs: Math.round(nowMs() - startedAt),
      });
    } catch (e) {
      if (e.name === "AbortError") {
        console.warn("[apiRequest] RETRY_ABORTED", {
          endpoint,
          method,
          totalMs: Math.round(nowMs() - startedAt),
        });
        throw e;
      }

      console.error("[apiRequest] REFRESH_OR_RETRY_FAILED", {
        endpoint,
        method,
        message: e?.message,
        status: e?.status,
        totalMs: Math.round(nowMs() - startedAt),
      });

      const error = new Error("再ログインが必要です");
      error.status = 401;
      throw error;
    }
  }

  if (!response.ok) {
    let errorDetails = {};
    try {
      errorDetails = await response.json();
    } catch {
      console.warn("[apiRequest] ERROR_BODY_PARSE_FAILED", {
        endpoint,
        method,
        status: response.status,
      });
    }

    console.error("[apiRequest] HTTP_ERROR", {
      endpoint,
      method,
      status: response.status,
      details: errorDetails,
      ms: Math.round(nowMs() - startedAt),
    });

    const error = new Error(`HTTP error! Status: ${response.status}`);
    error.status = response.status;
    error.details = errorDetails;
    throw error;
  }

  if (method === "DELETE" && response.status === 204) {
    console.log("[apiRequest] END_NO_CONTENT", {
      endpoint,
      method,
      ms: Math.round(nowMs() - startedAt),
    });
    return null;
  }

  const json = await response.json();

  console.log("[apiRequest] END", {
    endpoint,
    method,
    ms: Math.round(nowMs() - startedAt),
    responsePreview: Array.isArray(json)
      ? `array(${json.length})`
      : json && typeof json === "object"
      ? Object.keys(json)
      : json,
  });

  return json;
}

export async function apiRequestFile(
  endpoint,
  method = "POST",
  file = null,
  additionalData = null,
  retry = true,
  signal = null
) {
  const startedAt = nowMs();

  try {
    ensureAuthenticatedRequest(endpoint);
  } catch (error) {
    console.error("[apiRequestFile] AUTH_BLOCKED", {
      endpoint,
      method,
      message: error.message,
      status: error.status,
      skipRequest: error.skipRequest,
      ms: Math.round(nowMs() - startedAt),
    });
    throw error;
  }

  let accessToken = getAccessToken();

  const formData = new FormData();

  if (file) {
    if (Array.isArray(file)) {
      file.forEach((f) => {
        formData.append("images", f);
      });
    } else {
      formData.append("file", file);
    }
  }

  if (additionalData) {
    for (const [key, value] of Object.entries(additionalData)) {
      if (Array.isArray(value)) {
        value.forEach((item) => {
          formData.append(key, item);
        });
      } else if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    }
  }

  const headers = {};
  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  const options = {
    method,
    headers,
    body: formData,
    credentials: "include",
    signal,
  };

  console.log("[apiRequestFile] START", {
    endpoint,
    method,
    retry,
    hasAccessToken: !!accessToken,
    hasRefreshToken: !!getRefreshToken(),
    hasFile: !!file,
    fileCount: Array.isArray(file) ? file.length : file ? 1 : 0,
    additionalData: safePreview(additionalData),
  });

  let response;

  try {
    response = await fetch(`${API_URL}${endpoint}`, options);
  } catch (error) {
    console.error("[apiRequestFile] FETCH_ERROR", {
      endpoint,
      method,
      message: error?.message,
      name: error?.name,
      ms: Math.round(nowMs() - startedAt),
    });
    throw error;
  }

  console.log("[apiRequestFile] FIRST_RESPONSE", {
    endpoint,
    method,
    status: response.status,
    ok: response.ok,
    ms: Math.round(nowMs() - startedAt),
  });

  if (response.status === 401 && retry && !isPublicEndpoint(endpoint)) {
    console.warn("[apiRequestFile] 401_DETECTED -> TRY_REFRESH", {
      endpoint,
      method,
      ms: Math.round(nowMs() - startedAt),
    });

    try {
      accessToken = await getRefreshedToken(signal);
      headers["Authorization"] = `Bearer ${accessToken}`;

      const retryStartedAt = nowMs();
      response = await fetch(`${API_URL}${endpoint}`, {
        method,
        headers,
        body: formData,
        credentials: "include",
        signal,
      });

      console.log("[apiRequestFile] RETRY_RESPONSE", {
        endpoint,
        method,
        status: response.status,
        ok: response.ok,
        retryMs: Math.round(nowMs() - retryStartedAt),
        totalMs: Math.round(nowMs() - startedAt),
      });
    } catch (e) {
      if (e.name === "AbortError") {
        console.warn("[apiRequestFile] RETRY_ABORTED", {
          endpoint,
          method,
          totalMs: Math.round(nowMs() - startedAt),
        });
        throw e;
      }

      console.error("[apiRequestFile] REFRESH_OR_RETRY_FAILED", {
        endpoint,
        method,
        message: e?.message,
        status: e?.status,
        totalMs: Math.round(nowMs() - startedAt),
      });

      const error = new Error("再ログインが必要です");
      error.status = 401;
      throw error;
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    console.error("[apiRequestFile] HTTP_ERROR", {
      endpoint,
      method,
      status: response.status,
      details: errorData,
      ms: Math.round(nowMs() - startedAt),
    });

    const error = new Error(`HTTP error! Status: ${response.status}`);
    error.status = response.status;
    error.details = errorData;
    throw error;
  }

  if (method === "DELETE" && response.status === 204) {
    console.log("[apiRequestFile] END_NO_CONTENT", {
      endpoint,
      method,
      ms: Math.round(nowMs() - startedAt),
    });
    return null;
  }

  const json = await response.json();

  console.log("[apiRequestFile] END", {
    endpoint,
    method,
    ms: Math.round(nowMs() - startedAt),
    responsePreview: Array.isArray(json)
      ? `array(${json.length})`
      : json && typeof json === "object"
      ? Object.keys(json)
      : json,
  });

  return json;
}